// Shared pieces of the Craftsman Christmas website: header, string of lights,
// API calls, toasts, and the how-to guides shown as pop-ups.
//
// Any element with data-guide="<id>" opens that guide when clicked.

// The site's look, set by the admin (hub/season.ts marks the page).
export const HALLOWEEN = document.documentElement.dataset.season === "halloween";
export const SITE_NAME = HALLOWEEN ? "Craftsman Halloween" : "Craftsman Christmas";

export interface Me {
  signedIn: boolean;
  street?: string;
  role?: "admin" | "resident";
  admin?: boolean;
  status?: "pending" | "approved";
  name?: string;
  house?: { id: string; address: string } | null;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? `Something went wrong (${res.status}).`);
  return data as T;
}

export async function getMe(): Promise<Me | null> {
  try {
    return await api<Me>("GET", "/api/me");
  } catch {
    return null; // no street set up on this hub (development)
  }
}

export function $<T extends HTMLElement = HTMLElement>(selector: string, root: ParentNode = document): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`missing element ${selector}`);
  return el;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.append(...children);
  return node;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(message: string): void {
  document.querySelector(".toast")?.remove();
  const box = el("div", { class: "toast", role: "status" }, message);
  document.body.append(box);
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => box.remove(), 3500);
}

// The string of lights across the top of every page.
export function drawLights(container: Element): void {
  const colors = HALLOWEEN
    ? ["#ff7a1a", "#9b4dff", "#8bd450", "#ff7a1a", "#c56bff", "#ffb627"]
    : ["#e53935", "#43a047", "#fbc02d", "#1e88e5", "#fb8c00", "#d81b60"];
  // About one bulb every 32px, so phones get a neat string rather than a blur.
  const count = Math.max(10, Math.round((container.clientWidth || window.innerWidth) / 32));
  for (let i = 0; i < count; i++) {
    const bulb = document.createElement("span");
    bulb.style.left = `${(i / count) * 100 + 1}%`;
    bulb.style.setProperty("--c", colors[i % colors.length]!);
    bulb.style.setProperty("--d", `${((i * 7) % 13) * 0.2}s`);
    container.append(bulb);
  }
  if (!HALLOWEEN) return;
  // A spider hanging on its thread between two bulbs, about a third of the way in.
  const spider = document.createElement("span");
  spider.className = "spider";
  spider.style.left = `${((Math.floor(count / 3) + 0.5) / count) * 100 + 1}%`;
  spider.innerHTML =
    '<svg viewBox="0 0 14 30"><path d="M7 0v19" stroke="#d9d0e3" stroke-width=".6"/>' +
    '<g stroke="#e4d8f0" stroke-width="1.1" fill="none" stroke-linecap="round">' +
    '<path d="M5 22l-3-2-1 2M5 24l-4 0M5 25l-3 2-1 2M9 22l3-2 1 2M9 24l4 0M9 25l3 2 1 2"/></g>' +
    '<ellipse cx="7" cy="24" rx="3" ry="3.6" fill="#e4d8f0"/><circle cx="7" cy="20" r="2" fill="#e4d8f0"/>' +
    '<circle cx="6.2" cy="19.7" r=".6" fill="#ff3b1a"/><circle cx="7.8" cy="19.7" r=".6" fill="#ff3b1a"/></svg>';
  container.append(spider);
}

// Header links depend on who's signed in.
export async function setupHeader(me: Me | null): Promise<void> {
  document.querySelectorAll(".lights-string").forEach(drawLights);
  const nav = document.querySelector(".site-nav");
  const street = document.querySelector("[data-street-name]");
  if (street && me?.street) street.textContent = me.street;
  if (!nav) return;
  const links: HTMLElement[] = [];
  if (me?.signedIn) {
    if (me.house) links.push(el("a", { class: "button", href: "/neighborhood/house" }, "My house"));
    if (me.admin) links.push(el("a", { class: "button", href: "/neighborhood/admin" }, "Admin"));
    const out = el("button", { class: "link-button", type: "button" }, "Sign out");
    out.addEventListener("click", async () => {
      await api("POST", "/api/logout").catch(() => {});
      location.href = "/neighborhood";
    });
    links.push(out);
  }
  // Signed out: no header button; the neighborhood page's form is the one sign-in.
  nav.replaceChildren(...links);
}

// ---------------------------------------------------------------------------
// How-to guides

interface Guide {
  title: string;
  html: string;
}

export const GUIDES: Record<string, Guide> = {
  "how-it-works": {
    title: `How ${SITE_NAME} works`,
    html: `
      <p>Every house that joins gets a small box (a Raspberry Pi) that plugs into its internet router.
      The box talks to that house's Govee lights inside the home, and listens to our street's show over the internet.</p>
      <p>When Brian plays a song, every box knows exactly when each beat lands, so every house lights up
      at the same moment. If the internet blips, each box keeps the show going on its own clock.</p>
      <p>The music's beats are worked out ahead of time, so the timing is exact rather than guessed from a microphone.</p>`,
  },
  "lan-control": {
    title: "Turn on LAN Control in the Govee app",
    html: `
      <p>This lets your box talk to your lights inside your home. Do it once for each strand.</p>
      <ol>
        <li>Open the <strong>Govee Home</strong> app on your phone.</li>
        <li>Tap the light strand.</li>
        <li>Tap the <strong>gear icon</strong> (Settings), usually at the top right.</li>
        <li>Find <strong>LAN Control</strong> and switch it <strong>on</strong>.</li>
        <li>Go back and repeat for each strand you'd like in the show.</li>
      </ol>
      <p class="tip">Don't see LAN Control? Unplug that strand for a few minutes, plug it back in, and reopen the app.
      If it still isn't there, that model may not support it; let Brian know.</p>`,
  },
  "plug-in": {
    title: "Plug in your Craftsman Christmas box",
    html: `
      <ol>
        <li>Find your internet router (the box your internet comes in through).</li>
        <li>Connect the Craftsman Christmas box to the router with the network cable. Any free numbered port works.</li>
        <li>Plug the box into a power outlet. A small light comes on.</li>
        <li>Give it two minutes. This page will say <strong>Your box is online</strong>.</li>
      </ol>
      <p class="tip">Keep the box plugged in for the season. It updates itself, and it only talks to your lights and to our street's website.</p>`,
  },
  "box-offline": {
    title: "Box not showing up?",
    html: `
      <p>Try these in order. After each one, give it two minutes and check this page again.</p>
      <ol>
        <li><strong>Is it on?</strong> A small light on the box should be lit. If not, check that the power plug is in firmly and the outlet works (not one switched off at the wall).</li>
        <li><strong>Is the cable in the right port?</strong> On the router, use one of the <strong>numbered</strong> ports (often labeled 1–4 or LAN).
        Not the one marked <strong>Internet</strong>, <strong>WAN</strong> or with a globe, which is often a different color. Push both ends in until they click.</li>
        <li><strong>Are the port lights blinking?</strong> Where the cable goes into the box, a small green or orange light should flicker. No light means the cable or port isn't working: try another numbered port or another cable.</li>
        <li><strong>Restart it.</strong> Unplug the box's power, wait 10 seconds, and plug it back in.</li>
        <li><strong>Is your internet working?</strong> If other things at home are offline too, the box will come back on its own once the internet does.</li>
      </ol>
      <p class="tip">Still offline? Text Brian. He can see when your box last checked in, and can come by.</p>`,
  },
  modes: {
    title: "Who runs your lights?",
    html: `
      <ul>
        <li><strong>Admin control:</strong> Brian runs your lights with the street's show, in step with every other house.</li>
        <li><strong>My own:</strong> you choose your own colors and effects, or program each song yourself.</li>
        <li><strong>Off:</strong> the show leaves your lights alone. Use the Govee app as usual.</li>
      </ul>
      <p>You can switch any time, and it takes effect within a second. Nobody else can change this for your house. (Brian could only by giving you a new one-time code, which signs you out everywhere, so you'd know.)</p>`,
  },
  "own-show": {
    title: "My own show",
    html: `
      <p>In <strong>My own</strong>, your lights get their instructions from two places:</p>
      <ul>
        <li><strong>During a song:</strong> your light board decides what each strand does.
          Songs you haven't set up there play red and green on the beat.</li>
        <li><strong>When no song is playing:</strong> this card decides. That's between songs, and any time
          the street show isn't on.</li>
      </ul>
      <p>Pick an effect, colors and brightness, then tap <strong>Show it on my lights</strong>.
      Choose <strong>Lights off</strong> to keep them dark until the next song.</p>
      <p class="tip">Only used in <strong>My own</strong>. In Admin control, Brian's street show runs your lights instead.</p>`,
  },
  strands: {
    title: "Your strands",
    html: `
      <p>Your box lists every Govee light strand it can see on your home network, however many you have.</p>
      <ul>
        <li><strong>In the show:</strong> tick the strands you'd like in the show. Strands you don't tick are never touched.</li>
        <li><strong>Name:</strong> call each one what it is, like <em>Roofline</em>, <em>Bush</em> or <em>Porch rail</em>,
          so you and Brian can tell them apart.</li>
        <li><strong>Order (↑ ↓):</strong> effects like Run and Fill travel from strand to strand in this order, then on to the
          next house. Put them in the order someone walking past sees them, for example left to right.</li>
        <li><strong>Runs backwards:</strong> if an effect runs the wrong way along a strand, turn this on.</li>
        <li><strong>Fast mode:</strong> instant color changes, on models that support it.
          <button class="link-button" type="button" data-guide="fast-mode">More about fast mode</button></li>
      </ul>
      <p class="tip">A strand missing from the list? Make sure <strong>LAN Control</strong> is on for it in the Govee app,
      and that it's on your home Wi-Fi (not a guest network).</p>`,
  },
  "fast-mode": {
    title: "Fast mode (real-time)",
    html: `
      <p>Normally Govee lights fade gently from one color to the next. In fast mode the box drives the strand directly,
      so colors snap on the beat, and effects like Run and Twinkle can light different parts of the strand at once.</p>
      <p>It works on some models, like the 2-in-1 string and ornament lights (H6860), and not on others.
      Models we know work, like the H6860, start with fast mode on.</p>
      <ol>
        <li>Turn fast mode on for a strand while the show is running.</li>
        <li>Watch that strand for a few seconds.</li>
        <li>If it stays dark, freezes, or doesn't change, turn fast mode off. That model doesn't support it.</li>
      </ol>
      <p class="tip">With fast mode off, the strand still joins in: it flashes and changes color with everyone else,
      just a little softer. Effects that run along the lights (like Comet) become a whole-strand flash; the strip says so
      and has a <strong>Turn on fast mode</strong> button.</p>`,
  },
  board: {
    title: "Your light board",
    html: `
      <p>The light board decides what your lights do during each song of the street show. Think of it as a small
      mixing board, with one <strong>strip</strong> per strand of lights.</p>
      <p><strong>My strands</strong>, at the top, lists the strands your box found: tick the ones in the show, name them
      and put them in order. Each one gets its own strip below.</p>
      <ul>
        <li><strong>All other strands</strong> (optional switch above the strips): one extra strip for any strand without its own,
          like lights your box finds later.</li>
        <li><strong>Effect:</strong> the pattern, like a new color on every beat, a soft pulse, or a steady glow.
          Run, Comet, Marquee, Bounce, Crossing lights, Fill, Alternate, Twinkle and Rainbow light parts of a strand
          separately, on lights in fast mode.</li>
        <li><strong>Moves with:</strong> what in the music makes the lights brighter. <em>Beat</em> keeps them at full
          brightness and lets the effect follow the beat; <em>Melody</em> glows with the tune; <em>Volume</em> with how loud it is;
          <em>Bass</em> with the drums; <em>Treble</em> with bells and cymbals; <em>Steady</em> ignores the music.</li>
        <li><strong>Intensity:</strong> how much the music moves the brightness. <strong>Brightness:</strong> the strip's top level.</li>
        <li><strong>Colors:</strong> pick a palette or add your own. <em>Color follows the notes</em> gives each note of the tune its own color.</li>
        <li><strong>Speed:</strong> how often the effect changes, from 0× (still) to 3× (three times a beat); 1× is every beat.</li>
        <li><strong>Contrast:</strong> how dark the unlit parts get: high is crisp on and off, low keeps a soft glow.</li>
      </ul>
      <p><strong>Quick start:</strong> <em>Auto: beat</em> changes color on every beat and gets brighter as the music gets louder.
      <em>Auto: melody</em> lets the tune pick the colors. Choose your colors first; then adjust anything you like.</p>
      <p><strong>Song tags:</strong> change the look partway through a song. <em>Add a tag here</em> puts a tag on the player's
      timeline where the song is; from that moment until the next tag, the tag's own look plays. Tap a tag to edit its look in the
      strips, drag it to move it. <em>Tags at each part</em> adds one wherever the song changes (a verse, a chorus).
      Tags belong to one song, so they're saved with <em>Save for this song</em>.</p>
      <p><strong>Try it on my lights</strong> plays the song on this phone and your lights follow the board, so you can see each change.
      This works even if your house is in Admin control, and only while you're trying.</p>
      <p><strong>Program with the current show</strong> (the switch at the top of <em>Program your lights</em>): while the street show
      plays a song, the board switches to it and your board plays on your lights and here, in time with the show's music.</p>
      <p><strong>Saving:</strong> <em>Save for this song</em> keeps the board for that song. <em>Save as my default</em> plays it for every
      song without its own. Your programs play during the street show when your house is set to <strong>My own</strong>.
      A song with nothing saved (no program of its own and no default) plays <em>Auto: beat</em> in red and green.</p>
      <p class="tip">The preview on the page shows the board with the music even without your box, so you can plan from anywhere.</p>`,
  },
  "another-phone": {
    title: "Signing in on another phone",
    html: `
      <p>On the other phone or computer, open this same neighborhood page and sign in with your house number and password.
      You can be signed in on as many devices as you like.</p>
      <p>Forgot your password? Ask Brian for a new one-time code, then use <strong>First time?</strong> again.</p>`,
  },
  privacy: {
    title: "Privacy and safety",
    html: `
      <ul>
        <li>We only know your house number. We never ask for your Govee account or password.</li>
        <li>Your box only talks to your lights inside your home, and to our street's website. It doesn't look at anything else on your network.</li>
        <li>Each house has its own login, set with a one-time code from Brian. You can only change your own house.</li>
        <li>Passwords are stored scrambled (hashed); nobody, including Brian, can read yours.</li>
      </ul>`,
  },
  security: {
    title: "Is this safe for my home?",
    html: `
      <p>Yes. Here's the short version.</p>
      <ul>
        <li><strong>The box only talks to your Govee lights and our street's website.</strong>
          A built-in firewall stops it reaching anything else in your home: your computers, phones, cameras or router.</li>
        <li><strong>Nobody on the internet can reach the box.</strong> It only calls out to our website,
          the way your phone checks for email. Nothing on your router needs changing.</li>
        <li><strong>Your Wi-Fi password stays yours.</strong> The box plugs into your router with a cable.
          If you'd rather use Wi-Fi, you type the password in yourself; Brian never sees it.</li>
        <li><strong>Only updates Brian signed get in.</strong> The box installs new software only if it's signed with a key
          that never leaves Brian's computer. Even someone who broke into the website couldn't put bad software on your box.</li>
        <li><strong>You're in charge of your lights.</strong> You pick which strands join and who runs them, and you can choose
          <strong>Off</strong> any time. Your login only works for your house.</li>
        <li><strong>We know very little about you:</strong> your house number and which Govee lights you have.
          No names, emails, or Govee account.</li>
        <li><strong>Unplug the box and everything stops.</strong></li>
      </ul>
      <p><strong>What Brian can do:</strong> run your lights when you choose <strong>Admin control</strong>; see which lights
        your box found and whether it's online; and sign in to the box to fix it, but only from inside your home network.
        If you'd rather Brian couldn't sign in to it, just ask. Brian can also give you a new one-time code, which signs you
        out everywhere, so you'd notice.</p>
      <p><strong>Flashing lights:</strong> shows can include fast flashing. If anyone at home is sensitive to flashing light,
        choose <strong>Off</strong> or <strong>My own</strong>.</p>
      <p class="hint">The technical details, for anyone who wants them:</p>
      <details class="faq">
        <summary>What the box connects to</summary>
        <p>The light helper (the program that runs your lights) can reach only:
          DNS; your Govee lights on UDP ports 4001 and 4003, on private home addresses; and HTTPS (port 443) on the internet,
          which is how it reaches craftsmanxmas.com over an encrypted WebSocket. A firewall (nftables) blocks it from every
          other address on your home network: private ranges, link-local, multicast, and your home's own subnets, including
          a public IPv6 /64. The box's operating system also fetches its own software updates.</p>
      </details>
      <details class="faq">
        <summary>What can reach the box</summary>
        <p>Nothing from the internet: it opens no ports on your router, and its firewall drops everything that isn't a reply to
          its own connections. From your home network it accepts only the Govee lights' answers (UDP 4002), local name lookups
          (mDNS) and SSH. SSH accepts only Brian's key, never a password.</p>
      </details>
      <details class="faq">
        <summary>How updates are protected</summary>
        <p>Each night the box checks craftsmanxmas.com for a new release. It installs one only if the release is signed with
          Brian's Ed25519 key (checked against the key saved on your box when it was set up), the download matches the
          signed fingerprint, the files are exactly the signed list, and it's newer than what's installed (so an old release
          can't be replayed). If the new version doesn't keep running, the box switches back by itself. The exact Node.js
          version it runs is part of the signed release too, checked by its SHA-256 fingerprint.</p>
      </details>
      <details class="faq">
        <summary>How the box is locked down</summary>
        <p>The light helper runs as its own user with no administrator rights, inside a systemd sandbox: it can't change the
          system, can't load drivers, and can't capture network traffic, so it can't see what your other devices do.
          Debian security updates install every night, and the box restarts at 4:30 AM if one needs it.</p>
      </details>
      <details class="faq">
        <summary>Your lights</summary>
        <p>The box only ever controls the strands you ticked; others are left alone. Because anything on a home network can
          pretend to be a Govee light, it believes a light only if the answer comes from that light's own address on your
          network, keeps at most 32 lights, and ignores floods of fake answers.</p>
      </details>
      <details class="faq">
        <summary>The website and your login</summary>
        <p>The site uses HTTPS only (with HSTS). Passwords are stored as scrypt hashes, so nobody, including Brian, can read
          yours. Sign-ins are secure, HTTP-only cookies. Repeated wrong passwords are slowed and locked out per address and per
          house. A house's login can only see and change that house. Changing your password signs out your other devices.</p>
      </details>
      <details class="faq">
        <summary>What we store</summary>
        <p>Your house number; your password, scrambled; your choices (mode, strands, colors and programs); the lights your box
          found (their Govee ID, model and address on your home network); when your box was last online; and the kind of
          device you signed in on (like "iPhone").</p>
      </details>
      <details class="faq">
        <summary>Honest limits</summary>
        <p>Govee's own LAN Control has no password: anything already on your Wi-Fi could control your Govee lights, with or
          without our box. That's how Govee designed it. Brian, as organizer, is trusted: Brian runs the website and can sign in
          to the box from inside your home network. Someone who took the box's memory card could read its device key; Brian can
          switch that key off from the admin page. Questions or worries? Ask Brian.</p>
      </details>`,
  },
  "admin-codes": {
    title: "House logins",
    html: `
      <ol>
        <li>Add the houses by number (the number is the household's username).</li>
        <li>On a house, choose <strong>Give login code</strong>. A one-time code appears (and can be printed as a card).</li>
        <li>Hand it to the household in person. They go to <strong>craftsmanxmas.com/neighborhood</strong>, choose
          <strong>First time?</strong>, enter their house number and the code, and pick their own password.</li>
      </ol>
      <p>Codes last 30 days and work once. <strong>Give login code</strong> again any time to reset a forgotten password; it signs that house out everywhere.</p>
      <p class="tip">Mark your own house as <strong>Admin</strong> to reach this console with your house login.</p>`,
  },
  "admin-pi": {
    title: "Preparing a Pi for a house",
    html: `
      <ol>
        <li>Set up the Pi's SD card with Raspberry Pi Imager (Raspberry Pi OS Lite 64-bit, SSH on). See the admin guide, Part D.</li>
        <li>Plug the Pi into your router and power, then <code>ssh ccadmin@cc-NAME.local</code>.</li>
        <li>Here, choose <strong>Prepare a Pi</strong> on that house. It shows a command and the Pi's device key.</li>
        <li>Paste the command on the Pi, then paste the key when it asks (it stays hidden, so it isn't saved on the Pi's command history). In a minute the house shows <strong>Online</strong>.</li>
        <li>Unplug it and take it to the house. It won't look for lights until it's plugged in there, and won't touch any until that household ticks theirs.</li>
      </ol>
      <p class="tip">Each Pi has its own device key, shown once. If a Pi is lost, revoke its key here.</p>`,
  },
  "admin-songs": {
    title: "Adding songs",
    html: `
      <p>Songs must be files without copy protection. The easiest way to get real songs:</p>
      <ol>
        <li>On a Mac, open the <strong>Music</strong> app → <strong>iTunes Store</strong> and buy the songs.</li>
        <li>Download them, then right-click → <strong>Show in Finder</strong>.</li>
        <li>Drag the files onto the Music panel here, then press <strong>Analyze all</strong>.</li>
      </ol>
      <p>Amazon MP3 purchases, Bandcamp downloads and CDs imported with the Music app work too. Spotify and Apple Music subscription downloads don't.</p>`,
  },
  "admin-public": {
    title: "The public Listen page",
    html: `
      <p>Anyone driving by can open the site, tap <strong>Listen</strong>, and hear the song that's playing, in step with the lights.</p>
      <p>Only songs you mark <strong>Public</strong> play there. Other songs still light up the street; listeners just see
      "This song is for the street only".</p>
      <p class="tip">Broadcasting a song to the public counts as a public performance. Buying a download doesn't include that right.
      Public-domain recordings (like U.S. military band albums) and Creative Commons songs are safe to share. For purchased hits,
      sharing is your call: the practical risk for a small, free neighborhood display is low, but it isn't zero.</p>
      <p>Only the song playing at that moment can be streamed, so nobody can download your library through the Listen page.</p>`,
  },
  "admin-show": {
    title: "Running a show",
    html: `
      <ol>
        <li>Open this page on the computer or phone connected to your speakers.</li>
        <li>Choose an effect, a speed and a palette in the Show panel. Section effects (Run, Fill,
          Alternate, Twinkle, Rainbow) need lights in real-time mode; other lights play the closest
          whole-strand effect.</li>
        <li>Click a song. Every house set to <strong>Admin control</strong> follows along.</li>
        <li>If a house looks early or late, nudge its offset in Houses.</li>
      </ol>
      <p>To run the show every evening without this page, use <strong>Show schedule</strong>.</p>`,
  },
  "admin-schedule": {
    title: "The show schedule",
    html: `
      <p>Turn on <strong>Run the show automatically</strong>, pick the hours and days, and save. During
        those hours the website plays songs one after another by itself:</p>
      <ul>
        <li>Houses in <strong>Admin control</strong> play your street program for each song (or
          "Auto: beat" in the street show's colors if you haven't made one). Houses in
          <strong>My own</strong> play their own programs.</li>
        <li>The public Listen page plays along (songs marked Public).</li>
        <li>The public page shows the hours: "The show plays between 5:00pm and 10:00pm".</li>
      </ul>
      <p><strong>In order, repeating</strong> follows the Music list's order. <strong>Shuffle</strong>
        plays every song once, in a random order, before any repeats.</p>
      <p><strong>Music for the street:</strong> on the computer or phone connected to your outdoor
        speakers, open this page and press <strong>Play the show here</strong> under Street speakers.</p>
      <p>A song that starts before the end time plays to its end. <strong>Stop for tonight</strong>
        stops right away and the show starts again at its next start time. Playing a song yourself from
        Music takes over; the schedule carries on after it.</p>`,
  },
};

// ---------------------------------------------------------------------------
// Step-by-step guides ("tours"): one short page at a time, with Back / Next,
// and "Show me" to jump to that part of the page.
//
// Any element with data-tour="<id>" opens that tour; data-tour-for="street"
// picks the street version of steps that differ.

type Audience = "house" | "street";

interface TourStep {
  title: string;
  html: string;
  // CSS selector of the part of the page this step is about.
  show?: string;
  // Only for households (house) or the admin's street board (street).
  only?: Audience;
}

// Little look-alikes of the page's buttons, so people recognize them.
const ui = (label: string) => `<span class="ui">${label}</span>`;

export const TOURS: Record<string, TourStep[]> = {
  board: [
    {
      title: "Welcome to your light board",
      only: "house",
      html: `
        <p>This is where you decide what your lights do during each song of the street show.
        Think of it as a small mixing board: pick a look, watch it with the music, and save it.</p>
        <p>The page goes from top to bottom:</p>
        <ol class="tour-map">
          <li><strong>My strands</strong>: which lights are in the show, their names and order.</li>
          <li><strong>Program your lights</strong>, all in one card:
            <ul>
              <li><strong>Program with the current show</strong>: an optional switch to program along with the street show.</li>
              <li><strong>Song</strong>: which song you're setting up (or one look for all songs).</li>
              <li><strong>Quick start</strong>: a good look in one tap.</li>
              <li><strong>Preview</strong>: your lights on screen, moving with the music.</li>
              <li><strong>Song tags</strong>: change the look partway through a song.</li>
              <li><strong>Strips</strong>: the detailed controls, one per strand of lights.</li>
              <li><strong>Save</strong>.</li>
            </ul>
          </li>
        </ol>
        <p>The <strong>player</strong> stays at the bottom of the screen: ${ui("▶")} plays the song.</p>
        <p class="tip">Nothing changes on your real lights until you save or press ${ui("Try it on my lights")}
        (or ${ui("Preview on my lights")} on a strip),
        so explore freely. This takes about two minutes; tap <strong>Next</strong>.</p>`,
    },
    {
      title: "Welcome to the street light board",
      only: "street",
      html: `
        <p>This decides what every house in <strong>Admin control</strong> does during each song.
        Think of it as a small mixing board: pick a look, watch it with the music, and save it.</p>
        <p>The page goes from top to bottom:</p>
        <ol class="tour-map">
          <li><strong>Song</strong>: which song you're setting up (or one look for all songs).</li>
          <li><strong>Quick start</strong>: a good look in one tap.</li>
          <li><strong>Preview</strong>: the lights on screen, moving with the music.</li>
          <li><strong>Song tags</strong>: change the look partway through a song.</li>
          <li><strong>Strip</strong>: the detailed controls.</li>
          <li><strong>Save</strong>.</li>
        </ol>
        <p>The <strong>player</strong> stays at the bottom of the screen: ${ui("▶")} plays the song.</p>
        <p class="tip">Nothing changes on the street until you save, so explore freely. Tap <strong>Next</strong>.</p>`,
    },
    {
      title: "My strands first",
      only: "house",
      show: ".strands-card",
      html: `
        <p>Every strand of lights your box found is listed here.</p>
        <ul>
          <li>Tick <strong>In the show</strong> for the ones the show may use. Strands you don't tick are never touched.</li>
          <li>Give each a name, like <em>Roofline</em>, so its strip below is easy to find.</li>
          <li>The arrows set the order: effects travel from strand to strand this way.</li>
        </ul>`,
    },
    {
      title: "Program with the current show",
      only: "house",
      show: ".follow-row",
      html: `
        <p>Want to program while the street show is on? Switch on ${ui("Program with the current show")}.</p>
        <ul>
          <li>When the show plays a song, the board switches to that song.</li>
          <li>Your board plays on your real lights <em>and</em> on the bulbs here, in time with the show's music.</li>
          <li>Change anything and your lights show it right away. Save to keep it for that song.</li>
          <li>When the song ends, the board picks up the next one (if you have unsaved changes, it waits until you save or revert).</li>
        </ul>
        <p class="tip">While it's on, the show sets the song and the time, so the song picker, ${ui("This song")} / ${ui("My default")}
        and ${ui("Try it on my lights")} are greyed out. ${ui("▶")} becomes ${ui("🔊")}: the show's music plays here, in time with the street;
        tap it to turn the music off or on. Your lights follow the board too; ${ui("■ Stop preview")} on any strip puts them back on their usual show while you keep working here, and ${ui("Preview with the show")} brings them back.
        Switch it off and your lights go back to their usual show.</p>`,
    },
    {
      title: "1. Choose what you're editing",
      show: ".board-pick",
      html: `
        <ul>
          <li><strong>Song:</strong> pick any song on the street's playlist.</li>
          <li>${ui("This song")}: what you set up plays for that song only. Song tags only work here.</li>
          <li>${ui("My default (all songs)")} (the <em>street default</em> on the street board): plays for every song
            that doesn't have its own program. A great place to start: one look for the whole season, then give your favorite songs their own.</li>
        </ul>
        <p>Under each choice it says <em>Saved</em> or <em>Not saved yet</em>, so you can see what's set up.</p>`,
    },
    {
      title: "2. Quick start: a good look in one tap",
      show: ".quick-card",
      html: `
        <ol>
          <li>Tap a set of <strong>colors</strong> first (the row of swatches).</li>
          <li>Then tap one:
            <ul>
              <li>${ui("Auto: beat")}: a new color on every beat, brighter when the music gets louder.</li>
              <li>${ui("Auto: melody")}: calmer; the tune picks the colors and the lights glow with it.</li>
            </ul>
          </li>
        </ol>
        <p>Quick start fills in <strong>every strip</strong> on the board, replacing what was there.
        After that you can change anything in the strips.</p>`,
    },
    {
      title: "3. Play it and watch the preview",
      show: ".transport",
      html: `
        <ul>
          <li>${ui("▶")} at the bottom plays the song on this phone or computer. Drag the slider to jump to any part.</li>
          <li>The <strong>Preview</strong> shows a row of bulbs for each strand, doing exactly what the real lights will do.</li>
        </ul>
        <p class="tip">The preview works anywhere, even without the lights, so you can plan from the couch.</p>
`,
    },
    {
      title: "Try it on your real lights",
      only: "house",
      show: ".try-button",
      html: `
        <p>Press ${ui("Try it on my lights")} and your real lights follow the board while the song plays here.
        Stand where you can see them, and change things as it plays.</p>
        <ul>
          <li>Each strip has the same button, ${ui("Preview on my lights")}, so you don't have to scroll back down.
          You don't need to save to see a change; save once you like it.</li>
          <li>It works even when your house is in <strong>Admin control</strong>, and only while you're trying.</li>
          <li>Press ${ui("■ Stop")} when you're done; your lights go back to the street show.</li>
          <li>Your box needs to be online (it says so on <strong>My house</strong>).</li>
        </ul>`,
    },
    {
      title: "4. Strips: one for each strand",
      only: "house",
      show: "#strips",
      html: `
        <p>Each <strong>strip</strong> is a column of controls for a strand of lights.</p>
        <ul>
          <li>Every strand your box found has its own strip, so your roofline can do something different from your bushes.</li>
          <li>Optional: switch on ${ui("All other strands")} for one extra strip that plays on any strand without its own,
            like lights your box finds later. With it on, ${ui("Remove strip")} hands a strand over to it.</li>
        </ul>
        <p class="tip">Strand names and order come from <strong>My strands</strong>, near the top of this page.
        Name them (like <em>Roofline</em>) so they're easy to tell apart here.</p>`,
    },
    {
      title: "4. The strip",
      only: "street",
      show: "#strips",
      html: `
        <p>The street board has one strip, ${ui("All strands")}: it plays on every strand of every house in
        <strong>Admin control</strong>. The next steps explain its controls.</p>`,
    },
    {
      title: "5. Effect: the pattern",
      show: "#strips",
      html: `
        <p><strong>Whole strand</strong> (all the lights together):</p>
        <dl class="tour-terms">
          <dt>Steady glow</dt><dd>One color, no beat pattern. Lovely with <em>Color follows the notes</em>.</dd>
          <dt>Color step</dt><dd>A new color on every beat.</dd>
          <dt>Street chase</dt><dd>Colors travel from house to house down the street.</dd>
          <dt>Soft pulse</dt><dd>A gentle swell on each beat.</dd>
          <dt>Hard flash</dt><dd>Snaps bright on the beat, dark between.</dd>
          <dt>Strobe</dt><dd>Short, sharp flashes.</dd>
        </dl>
        <p><strong>Sections</strong> (parts of the strand move separately):</p>
        <dl class="tour-terms">
          <dt>Run</dt><dd>A lit section runs along the lights.</dd>
          <dt>Comet</dt><dd>A light runs along with a long glowing tail.</dd>
          <dt>Marquee</dt><dd>Several lights chase together, evenly spaced.</dd>
          <dt>Bounce</dt><dd>A light bounces back and forth.</dd>
          <dt>Crossing lights</dt><dd>Two lights run toward each other and cross.</dd>
          <dt>Fill</dt><dd>Each color fills the lights from one end.</dd>
          <dt>Alternate</dt><dd>Every other section swaps colors.</dd>
          <dt>Twinkle</dt><dd>Random sections sparkle.</dd>
          <dt>Rainbow flow</dt><dd>Colors flow along the lights.</dd>
        </dl>
        <p class="tip">Section effects need <strong>fast mode</strong> on that strand (<strong>My strands</strong>, near the top of this page).
        Strands without it still join in with the closest whole-strand effect.</p>`,
    },
    {
      title: "6. Moves with, intensity and brightness",
      show: "#strips",
      html: `
        <p><strong>Moves with</strong> is what in the music makes the lights brighter:</p>
        <dl class="tour-terms">
          <dt>Beat</dt><dd>Full brightness; the effect keeps the beat.</dd>
          <dt>Melody</dt><dd>Brightest while a clear tune plays, dimmer between phrases.</dd>
          <dt>Volume</dt><dd>Brighter when the music is louder.</dd>
          <dt>Bass</dt><dd>Brightens with the drums and bass.</dd>
          <dt>Treble</dt><dd>Brightens with cymbals, bells and high notes.</dd>
          <dt>Steady</dt><dd>Ignores the music: always at the brightness you set.</dd>
        </dl>
        <ul>
          <li><strong>Intensity:</strong> how much the music changes the brightness. Low is subtle; high is big swings.</li>
          <li><strong>Brightness:</strong> the strip's top level.</li>
        </ul>
        <p class="tip">Calm and pretty: <em>Steady glow</em> moving with <em>Melody</em>.
        Party: <em>Hard flash</em> moving with <em>Bass</em>.</p>`,
    },
    {
      title: "7. Colors, speed and contrast",
      show: "#strips",
      html: `
        <ul>
          <li><strong>Colors:</strong> tap a palette, or ${ui("+")} to add a color of your own.
            Tap a color to remove it.</li>
          <li><strong>Color follows the notes:</strong> each note of the tune gets its own color;
            your colors fill in between.</li>
          <li><strong>Speed:</strong> a slider from 0× to 3×, against the beat. 1× changes on every beat,
            0.5× every other beat, 2× twice a beat. Slow it way down (like 0.1×) to watch a comet or run
            travel the whole strand; 0× holds the effect still.</li>
          <li><strong>Contrast:</strong> how dark the unlit parts get. High is crisp on and off with darkness
            between; low keeps a soft glow behind a comet or between flashes.</li>
        </ul>`,
    },
    {
      title: "8. Song tags: change the look mid-song",
      show: ".cues-card",
      html: `
        <p>Tags let a song start calm and go big at the chorus. Each tag starts a new look,
        which plays until the next tag. (Tags belong to one song, so choose ${ui("This song")} at the top.)</p>
        <ol>
          <li>Play the song, or drag the slider, to where the new look should start.</li>
          <li>Tap ${ui("+ Add a tag here")}. The tag starts as a copy of the look playing there.</li>
          <li>Change the strips. You're now editing that tag's look.</li>
        </ol>
        <p>Or tap ${ui("Tags at each part")} to add a tag wherever the song changes (verse, chorus…) in one go.
        Small letters on the player's timeline mark the parts.</p>
        <ul>
          <li><strong>Which look am I editing?</strong> Tap ${ui("Start · 0:00")} (the beginning of the song) or a tag like
            ${ui("Tag 1 · 0:45")}. The line underneath says exactly which part of the song you're editing.</li>
          <li><strong>Move a tag:</strong> drag its marker on the player's timeline.</li>
          <li><strong>Remove a tag:</strong> tap it, then ${ui("Remove this tag")}.</li>
        </ul>
        <p class="tip">A song can have up to 24 tags. They're saved with ${ui("Save for this song")}.</p>`,
    },
    {
      title: "9. Save",
      only: "house",
      show: ".save-row",
      html: `
        <ul>
          <li>${ui("Save for this song")}: keeps the board for the song you picked.</li>
          <li>${ui("Save as my default")}: plays for every song without its own program.</li>
          <li>${ui("Revert")}: drops your changes and goes back to what you last saved.</li>
          <li>${ui("Delete this song's program")}: that song goes back to your default.</li>
        </ul>
        <p>“Unsaved changes” appears next to <strong>Save</strong> when you've changed something, and the page asks
        before you leave without saving.</p>
        <p class="tip"><strong>Important:</strong> your programs play during the street show only when your house is set to
        <strong>My own</strong> (on My house, or with the ${ui("Switch to My own")} button at the top of this page).
        Songs with nothing saved play <em>Auto: beat</em> in red and green.</p>`,
    },
    {
      title: "9. Save",
      only: "street",
      show: ".save-row",
      html: `
        <ul>
          <li>${ui("Save for this song")}: keeps the board for the song you picked.</li>
          <li>${ui("Save as the street default")}: plays for every song without its own program.</li>
          <li>${ui("Revert")}: drops your changes and goes back to what you last saved.</li>
          <li>${ui("Delete this song's program")}: that song goes back to the street default.</li>
        </ul>
        <p>Saved programs play on every house in <strong>Admin control</strong>, during the show schedule
        and when you play songs from the admin console.</p>`,
    },
    {
      title: "You're ready",
      html: `
        <p>A simple way to start:</p>
        <ol>
          <li>Choose the <strong>default (all songs)</strong> at the top, pick your colors, and tap ${ui("Auto: beat")}.</li>
          <li>Press ${ui("▶")} and watch the preview.</li>
          <li>Save it as the default. Now every song has a look.</li>
          <li>Later, give your favorite songs their own look, with tags for the big moments.</li>
        </ol>
        <p>Open this guide again any time with ${ui("How to use the board")} at the top of the page.
        For a one-page summary, see <button class="link-button" type="button" data-guide="board">the light board at a glance</button>.</p>`,
    },
  ],
};

let tour: { id: string; steps: TourStep[]; index: number } | null = null;

export function openTour(id: string, audience: Audience = "house"): void {
  const steps = TOURS[id]?.filter((s) => !s.only || s.only === audience);
  if (!steps?.length) return;
  // Reopening after "Show me" carries on where they left off.
  if (tour?.id !== id || tour.steps.length !== steps.length) tour = { id, steps, index: 0 };
  dialog ??= createDialog();
  dialog.classList.add("tour");
  $(".dialog-foot", dialog).hidden = false;
  renderStep();
  if (!dialog.open) dialog.showModal();
}

function renderStep(): void {
  if (!tour || !dialog) return;
  const { steps, index } = tour;
  const step = steps[index]!;
  $("h2", dialog).textContent = step.title;
  const body = $(".dialog-body", dialog);
  // Tour HTML is our own static text above, never user input.
  body.innerHTML = step.html;
  if (step.show && document.querySelector(step.show)) {
    const show = el("button", { type: "button", class: "link-button tour-show" }, "Show me on the page");
    show.addEventListener("click", () => spotlight(step.show!));
    body.append(el("p", {}, show));
  }
  body.scrollTop = 0;

  const dots = steps.map((_, i) => {
    const dot = el("button", {
      type: "button",
      class: "tour-dot",
      "aria-label": `Step ${i + 1} of ${steps.length}`,
      "aria-current": i === index ? "step" : "false",
    });
    dot.addEventListener("click", () => goTo(i));
    return dot;
  });
  $(".tour-dots", dialog).replaceChildren(...dots);
  $(".tour-count", dialog).textContent = `${index + 1} of ${steps.length}`;
  $<HTMLButtonElement>(".tour-back", dialog).disabled = index === 0;
  $(".tour-next", dialog).textContent = index === steps.length - 1 ? "Done" : "Next →";
}

function goTo(index: number): void {
  if (!tour) return;
  if (index >= tour.steps.length) {
    tour.index = 0;
    dialog?.close();
    return;
  }
  tour.index = Math.max(0, index);
  renderStep();
}

// Close the guide, scroll to that part of the page and make it glow briefly.
function spotlight(selector: string): void {
  const target = document.querySelector<HTMLElement>(selector);
  dialog?.close();
  if (!target) return;
  const smooth = !matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "center" });
  target.classList.remove("tour-spot");
  void target.offsetWidth; // restart the glow if it's already showing
  target.classList.add("tour-spot");
  setTimeout(() => target.classList.remove("tour-spot"), 2600);
  toast("Tap “How to use the board” to carry on with the guide.");
}

// True the first time this browser sees `key` (false if storage is blocked,
// so a blocked browser isn't shown the guide on every visit).
export function firstVisit(key: string): boolean {
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

let dialog: HTMLDialogElement | null = null;

// Plain guides and dialogs hide the tour's Back / Next bar.
function plain(d: HTMLDialogElement): void {
  d.classList.remove("tour");
  $(".dialog-foot", d).hidden = true;
}

export function openGuide(id: string): void {
  const guide = GUIDES[id];
  if (!guide) return;
  dialog ??= createDialog();
  plain(dialog);
  $("h2", dialog).textContent = guide.title;
  // Guide HTML is our own static text above, never user input.
  $(".dialog-body", dialog).innerHTML = guide.html;
  if (!dialog.open) dialog.showModal();
}

export function openDialog(title: string, body: Node): HTMLDialogElement {
  dialog ??= createDialog();
  plain(dialog);
  $("h2", dialog).textContent = title;
  $(".dialog-body", dialog).replaceChildren(body);
  if (!dialog.open) dialog.showModal();
  return dialog;
}

function createDialog(): HTMLDialogElement {
  const close = el("button", { type: "button", class: "help", "aria-label": "Close" }, "✕");
  const back = el("button", { type: "button", class: "tour-back" }, "← Back");
  const next = el("button", { type: "button", class: "primary tour-next" }, "Next →");
  const d = el(
    "dialog",
    { "aria-labelledby": "dialog-title" },
    el("div", { class: "dialog-head" }, el("h2", { id: "dialog-title" }), close),
    el("div", { class: "dialog-body" }),
    el(
      "div",
      { class: "dialog-foot", hidden: "" },
      back,
      el("div", { class: "tour-progress" }, el("div", { class: "tour-dots" }), el("span", { class: "tour-count hint" })),
      next,
    ),
  );
  close.addEventListener("click", () => d.close());
  back.addEventListener("click", () => goTo((tour?.index ?? 0) - 1));
  next.addEventListener("click", () => goTo((tour?.index ?? 0) + 1));
  // Arrow keys step through a tour.
  d.addEventListener("keydown", (e) => {
    if (!d.classList.contains("tour") || !tour) return;
    if (e.key === "ArrowRight") goTo(Math.min(tour.index + 1, tour.steps.length - 1));
    else if (e.key === "ArrowLeft") goTo(tour.index - 1);
  });
  // Click outside the box closes it.
  d.addEventListener("click", (e) => {
    if (e.target === d) d.close();
  });
  document.body.append(d);
  return d;
}

// Wire up every data-guide and data-tour button on the page (including ones added later).
document.addEventListener("click", (e) => {
  const target = (e.target as Element | null)?.closest<HTMLElement>("[data-guide], [data-tour]");
  if (target?.dataset.guide) {
    e.preventDefault();
    openGuide(target.dataset.guide);
  } else if (target?.dataset.tour) {
    e.preventDefault();
    openTour(target.dataset.tour, target.dataset.tourFor === "street" ? "street" : "house");
  }
});

export function helpButton(guide: string, label: string): HTMLButtonElement {
  return el("button", { type: "button", class: "help", "data-guide": guide, "aria-label": label, title: label }, "?");
}
